import { useState } from "react"; // Added useState
import { motion, AnimatePresence } from "framer-motion";
import {
  PenLine,
  Copy,
  Save,
  Trash2,
  ChevronRight,
  Lock,
  FolderOpen,
  Download,
  Upload,
  FilePlus,
  Share2,
  FileScan,
  // Settings, // Removed for Advanced menu icon
} from "lucide-react";
import { Project } from "../types"; // Assuming Template type is defined here or passed differently
import { useAuth } from "../hooks/useAuth"; // Needed for requiresAuth

// Helper component for shortcut display
const ShortcutDisplay = ({ keys }: { keys: string }) => {
  // Check if running in a browser environment before accessing navigator
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const displayKeys = keys.replace('Cmd', isMac ? '⌘' : 'Ctrl');
  return <span className="ml-auto text-xs text-gray-500">{displayKeys}</span>;
};


// Define Template type locally if not imported from types/index.ts
interface Template {
  id: string;
  name: string;
  description: string;
}

interface ProjectMenuItemsProps {
  project: Project | null;
  templates: Template[];
  isPreviewMode: boolean;
  isUnsavedProject: boolean;
  isNotOwner: boolean;
  showTemplateMenu: boolean;
  // Handlers
  handleNewProject: () => void;
  handleSave: () => void;
  handleTemplateSelect: (templateId: string) => void;
  handleExportProject: () => void;
  handleImportProject: () => void;
  handleImportAuditCsv: () => void;
  handleClearCanvas: () => void;
  // State Setters for Dialogs/Menus
  setIsOpen: (isOpen: boolean) => void;
  setShowLoadProjectDialog: (show: boolean) => void;
  setShowRenameDialog: (show: boolean) => void;
  setShowSaveAsDialog: (show: boolean) => void; // Changed from setShowDuplicateDialog
  setShowDeleteProjectDialog: (show: boolean) => void;
  setShowTemplateMenu: (show: boolean) => void;
  setShowTemplateDialog: (show: boolean) => void;
  setShowSaveTemplateDialog: (show: boolean) => void;
  setShowShareDialog: (show: boolean) => void;
  // Other needed props
  showNotification: (type: 'success' | 'error' | 'info', message: string) => void; // Added showNotification
}

export default function ProjectMenuItems({
  project,
  templates,
  isPreviewMode,
  isUnsavedProject,
  isNotOwner,
  showTemplateMenu,
  handleNewProject,
  handleSave,
  handleTemplateSelect,
  handleExportProject,
  handleImportProject,
  handleImportAuditCsv,
  setIsOpen,
  setShowLoadProjectDialog,
  setShowRenameDialog,
  setShowSaveAsDialog, // Changed from setShowDuplicateDialog
  setShowDeleteProjectDialog,
  setShowTemplateMenu,
  setShowTemplateDialog,
  setShowSaveTemplateDialog,
  setShowShareDialog,
  showNotification, // Destructure showNotification
}: ProjectMenuItemsProps) {
  const { user, requiresAuth } = useAuth(); // Get user and requiresAuth hook
  const [showAdvancedMenu, setShowAdvancedMenu] = useState(false); // State for Advanced submenu

  return (
    <div className="py-1" role="menu">
      {/* --- Standard Menu Items --- */}
      <motion.button
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
        className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-2"
        onClick={handleNewProject}
      >
        <FilePlus className="h-4 w-4" /> New Project
        <ShortcutDisplay keys="Cmd+Alt+N" />
      </motion.button>

      {project !== null && project !== undefined && (
        <motion.button
          whileTap={!isPreviewMode && !isNotOwner ? { scale: 0.98 } : {}}
          transition={{ duration: 0.2 }}
          className={`w-full text-left px-3 py-1.5 text-sm ${isPreviewMode || isNotOwner ? 'text-gray-500 cursor-not-allowed' : 'text-gray-200 hover:bg-gray-700'} flex items-center gap-2`}
          onClick={handleSave}
          disabled={isPreviewMode || isNotOwner}
        >
          <Save className="h-4 w-4" /> {isUnsavedProject ? "Save Project" : "Save Changes"}
          {/* Conditionally render shortcut or lock */}
          {!(isPreviewMode || isNotOwner) && <ShortcutDisplay keys="Cmd+S" />}
          {(requiresAuth('save') || isPreviewMode || isNotOwner) && <Lock className="h-3 w-3 ml-auto text-gray-500" />}
        </motion.button>
      )}
      {/* Save As Button - Moved here */}
      {!!user && project && project.id !== 'unsaved-project' && (
          <motion.button
            whileTap={!isPreviewMode ? { scale: 0.98 } : {}}
            transition={{ duration: 0.2 }}
            className={`w-full text-left px-3 py-1.5 text-sm ${isPreviewMode ? 'text-gray-500 cursor-not-allowed' : 'text-gray-200 hover:bg-gray-700'} flex items-center gap-2`}
            onClick={() => { if (!isPreviewMode) { setShowSaveAsDialog(true); setIsOpen(false); } }}
            disabled={isPreviewMode}
          >
            <Copy className="h-4 w-4" /> Save As...
            {/* Shortcut for Save As: Cmd/Ctrl+Shift+S */}
            {!isPreviewMode && <ShortcutDisplay keys="Cmd+Shift+S" />}
            {isPreviewMode && <Lock className="h-3 w-3 ml-auto text-gray-500" />}
          </motion.button>
      )}

      {!requiresAuth('save') && (
        <motion.button
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-2"
          onClick={() => { setShowLoadProjectDialog(true); setIsOpen(false); }}
        >
          <FolderOpen className="h-4 w-4" /> Open Project
          <ShortcutDisplay keys="Cmd+O" />
        </motion.button>
      )}

      {/* --- Project Operations (if saved & permitted) --- */}
      {project !== null && project !== undefined && project.id !== 'unsaved-project' && !requiresAuth('save') && !isUnsavedProject && (
        <>
          <motion.button
            whileTap={!isPreviewMode && !isNotOwner ? { scale: 0.98 } : {}}
            transition={{ duration: 0.2 }}
            className={`w-full text-left px-3 py-1.5 text-sm ${isPreviewMode || isNotOwner ? 'text-gray-500 cursor-not-allowed' : 'text-gray-200 hover:bg-gray-700'} flex items-center gap-2`}
            onClick={() => { if (!isPreviewMode && !isNotOwner) { setShowRenameDialog(true); setIsOpen(false); } }}
            disabled={isPreviewMode || isNotOwner}
          >
            <PenLine className="h-4 w-4" /> Rename Project
            {(isPreviewMode || isNotOwner) && <Lock className="h-3 w-3 ml-auto text-gray-500" />}
          </motion.button>
          {/* Save As Button - Moved up */}
          <motion.button
            whileTap={!isPreviewMode && !isNotOwner ? { scale: 0.98 } : {}} transition={{ duration: 0.2 }}
            className={`w-full text-left px-3 py-1.5 text-sm ${isPreviewMode || isNotOwner ? 'text-gray-500 cursor-not-allowed' : 'text-gray-200 hover:bg-gray-700'} flex items-center gap-2`}
            onClick={() => { if (!isPreviewMode && !isNotOwner) { setShowDeleteProjectDialog(true); setIsOpen(false); } }}
            disabled={isPreviewMode || isNotOwner}
          >
            <Trash2 className="h-4 w-4" /> Delete Project
            {(isPreviewMode || isNotOwner) && <Lock className="h-3 w-3 ml-auto text-gray-500" />}
          </motion.button>
        </>
      )}

      <div className="border-t border-gray-700 my-1" />

      {/* --- Templates --- */}
      <div className="relative">
        <motion.button
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
          className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center justify-between group"
          onMouseEnter={() => setShowTemplateMenu(true)}
          onMouseLeave={() => setShowTemplateMenu(false)}
        >
          <div className="flex items-center gap-2">
            Load from a Template
          </div>
          <motion.div
            animate={{ x: showTemplateMenu ? 3 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="h-4 w-4" />
          </motion.div>
        </motion.button>
        {/* Submenu moved outside the button to prevent nesting */}
        <AnimatePresence>
          {showTemplateMenu && (
            <motion.div
              className="absolute left-full top-0 w-80 rounded-md shadow-lg bg-gray-900 dark:bg-gray-950 ring-1 ring-black ring-opacity-5" // Changed w-64 to w-80
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              onMouseEnter={() => setShowTemplateMenu(true)}
              onMouseLeave={() => setShowTemplateMenu(false)}
            >
              <div className="py-1">
                {templates.map((template: Template) => (
                  <motion.button
                    key={template.id}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-700"
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <div className="text-gray-200">{template.name}</div>
                    <div className="text-xs text-gray-400 line-clamp-1">
                      {template.description}
                    </div>
                  </motion.button>
                ))}
                <div className="border-t border-gray-700 my-1" />
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  className="w-full text-left px-3 py-1.5 text-sm text-blue-400 hover:bg-gray-700"
                  onClick={() => {
                    setShowTemplateDialog(true);
                    setIsOpen(false);
                    setShowTemplateMenu(false);
                  }}
                >
                  Browse all templates...
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Admin-only features */}
      {!requiresAuth('admin') && (
        <>
          {/* Save as Template */}
            <motion.button
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center justify-between"
              onClick={() => {
                setShowSaveTemplateDialog(true);
                setIsOpen(false);
              }}
            >
              <div className="flex items-center gap-2">
                Save as Template
              </div>
              <ShortcutDisplay keys="Cmd+Alt+S" />
              {/* Admin label removed */}
            </motion.button>

          {/* Divider after Save as Template */}
          <div className="border-t border-gray-700 my-1" />

          {/* Export Project - Removed from main menu, now in submenu */}
          {/* --- Advanced Admin Submenu --- */}
          <div className="relative">
            <motion.button
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center justify-between group"
              onMouseEnter={() => setShowAdvancedMenu(true)}
              onMouseLeave={() => setShowAdvancedMenu(false)}
            >
              <div className="flex items-center gap-2">
                {/* <Settings className="h-4 w-4" /> Removed icon */} Advanced
              </div>
              <div className="flex items-center gap-1">
                 {/* Admin label removed from submenu trigger */}
                <motion.div
                  animate={{ x: showAdvancedMenu ? 3 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight className="h-4 w-4" />
                </motion.div>
              </div>
            </motion.button>
            <AnimatePresence>
              {showAdvancedMenu && (
                <motion.div
                  className="absolute left-full top-0 w-56 rounded-md shadow-lg bg-gray-900 dark:bg-gray-950 ring-1 ring-black ring-opacity-5 z-10" // Changed w-64 to w-56
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  onMouseEnter={() => setShowAdvancedMenu(true)}
                  onMouseLeave={() => setShowAdvancedMenu(false)}
                >
                  <div className="py-1">
                    {/* Import Project */}
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-2 justify-between"
                      onClick={() => {
                        handleImportProject();
                        setIsOpen(false);
                        setShowAdvancedMenu(false);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Upload className="h-4 w-4" /> Import Project
                      </div>
                      <ShortcutDisplay keys="Cmd+I" />
                    </motion.button>
                    {/* Export Project */}
                    {project !== null && project !== undefined && project.id !== 'unsaved-project' && !isUnsavedProject && (
                       <motion.button
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-2 justify-between"
                        onClick={() => {
                          handleExportProject();
                          setIsOpen(false);
                          setShowAdvancedMenu(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <Download className="h-4 w-4" /> Export Project
                        </div>
                        <ShortcutDisplay keys="Cmd+E" />
                      </motion.button>
                    )}
                    {/* Import Audit CSV */}
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.2 }}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700 flex items-center gap-2 justify-between"
                      onClick={() => {
                        handleImportAuditCsv();
                        setIsOpen(false);
                        setShowAdvancedMenu(false);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <FileScan className="h-4 w-4" /> Import Audit CSV
                      </div>
                      <ShortcutDisplay keys="Cmd+Shift+I" />
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* --- END Advanced Admin Submenu --- */}
        </>
      )}

      <div className="border-t border-gray-700 my-1" />

      {/* Share Project - Moved here */}

        <motion.button
            whileTap={!isPreviewMode && !isNotOwner && !isUnsavedProject ? { scale: 0.98 } : {}}
            transition={{ duration: 0.2 }}
            className={`w-full text-left px-3 py-1.5 text-sm ${isPreviewMode || isUnsavedProject ? 'text-gray-500 cursor-not-allowed' : 'text-gray-200 hover:bg-gray-700'} flex items-center justify-between`}
            onClick={() => {
              if (isPreviewMode) {
                showNotification('info', 'You cannot share projects in preview mode');
                return;
              }
              if (isUnsavedProject) {
                showNotification('info', 'You need to save the project before sharing');
                return;
              }
              setShowShareDialog(true);
              setIsOpen(false)
            }}
            disabled={isPreviewMode || isUnsavedProject || (requiresAuth('save') && isUnsavedProject)}
          >
            <div className="flex items-center gap-2">
              <Share2 className="h-4 w-4" />
              Share Project
            </div>
            <div className="flex items-center">
              {(isPreviewMode || isUnsavedProject || (requiresAuth('save') && isUnsavedProject)) && <Lock className="h-3 w-3 ml-1 text-gray-500" />}
            </div>
        </motion.button>

    </div>
  );
}
